import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { MobileMenu } from '@/components/generals/mobile-menu'
import { MAIN_NAV } from '@/constants/navigation'

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const menuButtonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const update = () => setScrolled(window.scrollY > 16)

        update()
        window.addEventListener('scroll', update, { passive: true })

        return () => window.removeEventListener('scroll', update)
    }, [])

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const closeMenu = useCallback(() => {
        setMenuOpen(false)
        menuButtonRef.current?.focus()
    }, [])

    const isActive = (href: string): boolean => router.pathname === href || router.pathname.startsWith(`${href}/`)

    return (
        <>
            <header
                className={cn(
                    'fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-500 ease-out',
                    scrolled ? 'border-burgundy/10 bg-cream/85 backdrop-blur-xl' : 'border-transparent bg-transparent'
                )}
            >
                <div className='mx-auto flex h-16 w-full max-w-[1400px] items-center px-5 sm:px-8 md:h-[72px] lg:px-12'>
                    <Link href='/' aria-label='Veyra, back to home' className='rounded-md'>
                        <Logo />
                    </Link>

                    <nav aria-label='Main' className='ml-10 hidden items-center gap-0.5 lg:flex xl:ml-14'>
                        {MAIN_NAV.map(link => {
                            const active = isActive(link.href)

                            return (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    aria-current={active ? 'page' : undefined}
                                    className={cn(
                                        'relative rounded-full px-3.5 py-2 text-sm transition-colors duration-300',
                                        active ? 'text-burgundy' : 'text-burgundy/65 hover:text-burgundy'
                                    )}
                                >
                                    {link.label}

                                    <span
                                        aria-hidden='true'
                                        className={cn(
                                            'absolute bottom-0.5 left-1/2 size-1 -translate-x-1/2 rounded-full bg-gold-dark transition-opacity duration-300',
                                            active ? 'opacity-100' : 'opacity-0'
                                        )}
                                    />
                                </Link>
                            )
                        })}
                    </nav>

                    <div className='ml-auto hidden items-center gap-1.5 lg:flex'>
                        <Link
                            href='/login'
                            className='rounded-full px-4 py-2 text-sm text-burgundy/75 transition-colors duration-300 hover:text-burgundy'
                        >
                            Log in
                        </Link>

                        <ButtonLink href='/signup' size='sm'>
                            Sign up
                        </ButtonLink>
                    </div>

                    <button
                        ref={menuButtonRef}
                        type='button'
                        onClick={() => setMenuOpen(true)}
                        aria-expanded={menuOpen}
                        aria-controls='mobile-menu'
                        className='ml-auto inline-flex h-10 items-center gap-3 rounded-full border border-burgundy/15 bg-cream/40 pl-4 pr-3.5 text-sm font-medium text-burgundy backdrop-blur-sm transition-colors hover:border-burgundy/30 lg:hidden'
                    >
                        Menu

                        <span aria-hidden='true' className='flex flex-col gap-[4px]'>
                            <span className='block h-px w-4 bg-current' />
                            <span className='block h-px w-4 bg-current' />
                        </span>
                    </button>
                </div>
            </header>

            <MobileMenu open={menuOpen} onClose={closeMenu} />
        </>
    )
}
